import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { goToCustomerPortal } from '../config/stripeUrls'
import { fetchUpdates } from '../services/updatesService'
import { getUserProfile, updateUserProfile } from '../services/userDataService'
import { supabase } from '../supabase/supabaseClient'

const ONBOARDING_STEPS = [
  { id: 'education', title: 'Read the Education modules', shortTitle: 'Read the Education modules', desc: 'Learn the ins and outs of every strategy before diving in.', target: 'education', icon: 'education', accent: '#10b981' },
  { id: 'discord', title: 'Join our Discord community', shortTitle: 'Join our Discord', desc: 'Get access to automated bots, exclusive announcements and direct help from the team.', target: '__discord__', icon: 'discord', accent: '#5865F2' },
  { id: 'bookmaker', title: 'Sign up to a bookmaker & convert the deposit match', shortTitle: 'Convert a deposit match', desc: 'Start earning risk free cash by utilising the sign up deposit match offers most bookmakers provide.', target: 'bookmakers', icon: 'bookmakers', accent: '#f59e0b' },
  { id: 'first_ev', title: 'Place your first +EV bet', shortTitle: 'Place your first +EV bet', desc: 'Use the EV finder to spot a positive expected value bet', target: 'ev', icon: 'ev', accent: '#06b6d4' },
  { id: 'first_track', title: 'Track it in the Bet Tracker', shortTitle: 'Track your first bet', desc: "Track a bet directly from a tool's page or add one manually in the Bet Tracker.", target: 'tracker', icon: 'tracker', accent: '#ec4899' }
]

const SPOTLIGHT_TOOLS = [
  {
    id: 'ev', title: 'Positive Expected Value', cta: 'Open EV Finder',
    tagline: 'Find positive expected value bets across every major bookmaker. We calculate the true odds of every event and flag bookies offering higher. Watch your profit grow over a large sample size.',
    accent: '#10b981', accentSoft: 'rgba(16, 185, 129, 0.18)',
    bullets: ['100+ bookmakers', 'Sort by EV%', 'Real-time odds'],
    image: 'https://nfoqikkzbosxfylfstbz.supabase.co/storage/v1/object/public/images/featured_tools1/featured_ev.webp',
    imageMobile: 'https://nfoqikkzbosxfylfstbz.supabase.co/storage/v1/object/public/images/featured_tools1/featured_ev_mobile.webp'
  },
  {
    id: 'arb', title: 'Arbitrage Finder', cta: 'Open Arb Finder',
    tagline: 'Lock in risk-free profit by betting both sides of a market. Perfect for the risk averse. No losing bets, just guaranteed returns every time.',
    accent: '#a78bfa', accentSoft: 'rgba(167, 139, 250, 0.18)',
    bullets: ['Guaranteed profit', 'Stake calculator built-in', 'Updated every second'],
    image: 'https://nfoqikkzbosxfylfstbz.supabase.co/storage/v1/object/public/images/featured_tools1/featured_arb.webp',
    imageMobile: 'https://nfoqikkzbosxfylfstbz.supabase.co/storage/v1/object/public/images/featured_tools1/featured_arb_mobile.webp'
  },
  {
    id: 'bonus', title: 'Bonus Converter', cta: 'Open Bonus Converter',
    tagline: 'Turn deposit matches and bonus bets into withdrawable cash. Easily convert bonus bets at 70%+ retention every time.',
    accent: '#f59e0b', accentSoft: 'rgba(245, 158, 11, 0.18)',
    bullets: ['Convert any bonus bet', 'Maximise extraction rate', 'No risk to your bankroll'],
    image: 'https://nfoqikkzbosxfylfstbz.supabase.co/storage/v1/object/public/images/featured_tools1/featured_bonus.webp',
    imageMobile: 'https://nfoqikkzbosxfylfstbz.supabase.co/storage/v1/object/public/images/featured_tools1/featured_bonus_mobile.webp'
  },
  {
    id: 'odds', title: 'Sports Odds', cta: 'Open Sports Odds',
    tagline: 'Compare live odds across every bookie in one place. Spot the best price on any market without flicking between tabs.',
    accent: '#60a5fa', accentSoft: 'rgba(96, 165, 250, 0.18)',
    bullets: ['Side-by-side comparison', 'All sports, all markets', 'Spot the best line instantly'],
    image: 'https://nfoqikkzbosxfylfstbz.supabase.co/storage/v1/object/public/images/featured_tools1/featured_oddsscreen.webp',
    imageMobile: 'https://nfoqikkzbosxfylfstbz.supabase.co/storage/v1/object/public/images/featured_tools1/featured_oddsscreen_mobile.webp'
  },
  {
    // No mobile shot yet: mobile keeps the full-page PNG.
    id: 'horse_bot', title: 'Horse Racing Bot', cta: 'Open Racing Bot',
    tagline: 'Live +EV horse racing selections, sent 2 minutes before each race. EV picks to back and bonus picks to turn over, all in one feed.',
    accent: '#2dd4bf', accentSoft: 'rgba(45, 212, 191, 0.18)',
    bullets: ['+EV selections', 'Bonus turnover picks', 'Alerts before the jump'],
    image: 'https://nfoqikkzbosxfylfstbz.supabase.co/storage/v1/object/public/images/featured_tools1/featured_horsebot.webp',
    imageMobile: 'https://nfoqikkzbosxfylfstbz.supabase.co/storage/v1/object/public/images/featured_tools/horsebotpage.png'
  },
  {
    id: 'tracker', title: 'Bet Tracker', cta: 'Open Bet Tracker',
    tagline: 'Log every bet and watch your edge compound over time. Track every bet from EV, Arb, Promo and Middles in one clean dashboard.',
    accent: '#ec4899', accentSoft: 'rgba(236, 72, 153, 0.18)',
    bullets: ['ROI & win-rate stats', 'Profit chart by date', 'Filter by tool, sport, bookie'],
    image: 'https://nfoqikkzbosxfylfstbz.supabase.co/storage/v1/object/public/images/featured_tools1/featured_tracker.webp',
    imageMobile: 'https://nfoqikkzbosxfylfstbz.supabase.co/storage/v1/object/public/images/featured_tools1/MOBILE_TRACKER.webp'
  }
]


// A live count that rises from 0 once when it first lands (ease-out, ~0.7s),
// so the masthead strip reads as live. Instant under reduced motion. Counts
// of 1000 or more read "1000+".
function CountUp({ value }) {
  const [shown, setShown] = useState(0)
  useEffect(() => {
    const target = Math.min(value, 1000)
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setShown(target)
      return
    }
    let raf
    const start = performance.now()
    const tick = (t) => {
      const k = Math.min(1, (t - start) / 700)
      setShown(Math.round(target * (1 - Math.pow(1 - k, 3))))
      if (k < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [value])
  return <>{shown >= 1000 ? '1000+' : shown}</>
}

// Dashboard SVG Icons
const DashboardIcon = ({ name, size = 48, color = '#ffffff' }) => {
  const icons = {
    calculators: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="2" width="16" height="20" rx="2"/>
        <line x1="8" y1="6" x2="16" y2="6"/>
        <line x1="8" y1="10" x2="8" y2="10.01"/>
        <line x1="12" y1="10" x2="12" y2="10.01"/>
        <line x1="16" y1="10" x2="16" y2="10.01"/>
        <line x1="8" y1="14" x2="8" y2="14.01"/>
        <line x1="12" y1="14" x2="12" y2="14.01"/>
        <line x1="16" y1="14" x2="16" y2="14.01"/>
        <line x1="8" y1="18" x2="8" y2="18.01"/>
        <line x1="12" y1="18" x2="16" y2="18"/>
      </svg>
    ),
    tracker: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/>
        <line x1="12" y1="20" x2="12" y2="4"/>
        <line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
    ),
    ev: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
        <polyline points="17 6 23 6 23 12"/>
      </svg>
    ),
    arb: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="17 1 21 5 17 9"/>
        <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
        <polyline points="7 23 3 19 7 15"/>
        <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
      </svg>
    ),
    middles: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <circle cx="12" cy="12" r="6"/>
        <circle cx="12" cy="12" r="2"/>
      </svg>
    ),
    bonus: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 12 20 22 4 22 4 12"/>
        <rect x="2" y="7" width="20" height="5"/>
        <line x1="12" y1="22" x2="12" y2="7"/>
        <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
        <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
      </svg>
    ),
    pickem: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 11l3 3L22 4"/>
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
      </svg>
    ),
    prop_ev: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
        <polyline points="16 11 18 13 22 9"/>
      </svg>
    ),
    education: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
        <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5"/>
      </svg>
    ),
    odds: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2"/>
        <line x1="8" y1="21" x2="16" y2="21"/>
        <line x1="12" y1="17" x2="12" y2="21"/>
        <line x1="7" y1="8" x2="7" y2="12"/>
        <line x1="12" y1="6" x2="12" y2="12"/>
        <line x1="17" y1="9" x2="17" y2="12"/>
      </svg>
    ),
    bookmakers: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="8" y1="6" x2="21" y2="6"/>
        <line x1="8" y1="12" x2="21" y2="12"/>
        <line x1="8" y1="18" x2="21" y2="18"/>
        <line x1="3" y1="6" x2="3.01" y2="6"/>
        <line x1="3" y1="12" x2="3.01" y2="12"/>
        <line x1="3" y1="18" x2="3.01" y2="18"/>
      </svg>
    ),
    lock: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
    ),
    discord: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
      </svg>
    ),
    horse_bot: (
      <svg width={size} height={size} viewBox="0 0 512 512" fill={color}>
        <path d="M0 464V316.9C0 208.5 68.3 111.8 170.5 75.6L340.2 15.5C361.6 7.9 384 23.8 384 46.4c0 11-5.5 21.2-14.6 27.3L336 96c48.1 0 91.2 29.8 108.1 74.9l48.6 129.5c11.8 31.4 4.1 66.8-19.6 90.5c-16 16-37.8 25.1-60.5 25.1h-3.4c-26.1 0-50.9-11.6-67.6-31.7l-32.3-38.7c-11.7 4.1-24.2 6.4-37.3 6.4l-.1 0 0 0c-6.3 0-12.5-.5-18.6-1.5c-3.6-.6-7.2-1.4-10.7-2.3l0 0c-28.9-7.8-53.1-26.8-67.8-52.2c-4.4-7.6-14.2-10.3-21.9-5.8s-10.3 14.2-5.8 21.9c24 41.5 68.3 70 119.3 71.9l47.2 70.8c4 6.1 6.2 13.2 6.2 20.4c0 20.3-16.5 36.8-36.8 36.8H48c-26.5 0-48-21.5-48-48zM328 224c13.3 0 24-10.7 24-24s-10.7-24-24-24s-24 10.7-24 24s10.7 24 24 24z"/>
      </svg>
    )
  }
  return icons[name] || null
}

const dashboardStyles = {
  container: {
    padding: '0',
    backgroundColor: '#010714',
    minHeight: '100vh',
    color: '#f1f5f9'
  },
  welcomeSection: {
    marginBottom: '48px',
    textAlign: 'center',
    padding: '40px 40px 0 40px'
  },
  welcomeTitle: {
    fontSize: '42px',
    fontWeight: '700',
    margin: '0 0 16px 0',
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  welcomeSubtitle: {
    fontSize: '18px',
    color: '#94a3b8',
    maxWidth: '600px',
    margin: '0 auto 24px auto',
    lineHeight: '1.6'
  },
  userInfo: {
    fontSize: '16px',
    color: '#cbd5e1',
    margin: '16px 0'
  },
  planBadge: {
    display: 'inline-block',
    padding: '8px 16px',
    backgroundColor: '#2563eb',
    color: 'white',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
    marginTop: '8px',
    border: '2px solid #3b82f6',
    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 40px'
  },
  card: {
    background: 'linear-gradient(135deg, #2d3748 0%, #1e293b 100%)',
    border: '1px solid #475569',
    borderRadius: '12px',
    padding: '32px',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    textAlign: 'center'
  },
  cardIcon: {
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '600',
    margin: '0 0 8px 0',
    color: '#f1f5f9'
  },
  cardDescription: {
    fontSize: '14px',
    color: '#94a3b8',
    margin: '0 0 20px 0',
    lineHeight: '1.5'
  },
  cardButton: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
    color: '#f1f5f9',
    border: '1px solid #3b82f6',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif',
    transition: 'all 0.2s ease',
    width: '100%',
    letterSpacing: '0.01em',
    boxShadow: '0 2px 6px rgba(37, 99, 235, 0.25)'
  },
  lockedCard: {
    background: 'linear-gradient(135deg, #2d3748 0%, #1e293b 100%)',
    border: '1px solid #475569',
    borderRadius: '12px',
    padding: '32px',
    textAlign: 'center',
    opacity: 0.6
  },
  lockedButton: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #475569 0%, #334155 100%)',
    color: '#94a3b8',
    border: '1px solid #64748b',
    borderRadius: '8px',
    cursor: 'not-allowed',
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif',
    width: '100%',
    letterSpacing: '0.01em'
  },
  upgradeSection: {
    marginTop: '48px',
    textAlign: 'center',
    padding: '32px',
    background: 'linear-gradient(135deg, #2d3748 0%, #1e293b 100%)',
    border: '1px solid #475569',
    borderRadius: '12px',
    maxWidth: '600px',
    margin: '48px 40px 0 40px',
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  upgradeTitle: {
    fontSize: '24px',
    fontWeight: '600',
    margin: '0 0 12px 0',
    color: '#f1f5f9'
  },
  upgradeDescription: {
    fontSize: '16px',
    color: '#94a3b8',
    margin: '0 0 24px 0',
    lineHeight: '1.5'
  },
  buttonContainer: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  upgradeButton: {
    padding: '14px 28px',
    background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
    color: '#f1f5f9',
    border: '1px solid #3b82f6',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif',
    transition: 'all 0.2s ease',
    minWidth: '180px',
    letterSpacing: '0.01em',
    boxShadow: '0 2px 6px rgba(37, 99, 235, 0.25)'
  },
  manageButton: {
    padding: '14px 28px',
    background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
    color: '#f1f5f9',
    border: '1px solid #22c55e',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif',
    transition: 'all 0.2s ease',
    minWidth: '180px',
    letterSpacing: '0.01em',
    boxShadow: '0 2px 6px rgba(22, 163, 74, 0.25)'
  }
}

export default function Dashboard({ onNavigate, isVisible = true }) {
  const { user, subscriptionTier, hasAccess, hadTrial, loading: authLoading } = useAuth()
  const [updates, setUpdates] = useState([])
  const [updatesLoading, setUpdatesLoading] = useState(true)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640)
  const [onboarding, setOnboarding] = useState(null)
  const [justTickedId, setJustTickedId] = useState(null)
  const [spotlightIndex, setSpotlightIndex] = useState(0)
  const [spotlightPaused, setSpotlightPaused] = useState(false)
  const [liveStats, setLiveStats] = useState({ ev: null, arb: null, middle: null, bonus: null })
  const [liveStatsAt, setLiveStatsAt] = useState(null)   // set once the counts have landed (skeletons until then)
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 640)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  useEffect(() => {
    const loadUpdates = async () => {
      try {
        const data = await fetchUpdates(5)
        setUpdates(data)
      } catch (error) {
        console.error('Error loading updates:', error)
      } finally {
        setUpdatesLoading(false)
      }
    }

    loadUpdates()
  }, [])

  useEffect(() => {
    if (!user?.id) return
    const loadOnboarding = async () => {
      const { data } = await getUserProfile(user.id)
      setOnboarding(data?.onboarding_progress || {})
    }
    loadOnboarding()
  }, [user?.id])

  // Live opportunity counts for the masthead strip (desktop only). Uses the
  // same proxies as the tool pages; free users see counts as an upgrade tease.
  // Fetched once each time the dashboard is opened, never on a timer: the
  // dashboard stays mounted behind every other page, and a 2 min poll here was
  // most of our Supabase log ingestion.
  useEffect(() => {
    if (isMobile || !user?.id || !isVisible) return
    let cancelled = false
    const loadLiveStats = async () => {
      const [ev, arb, middle, bonus] = await Promise.allSettled([
        supabase.functions.invoke('ev-proxy'),
        supabase.functions.invoke('arb-proxy'),
        supabase.functions.invoke('middle-proxy'),
        supabase.functions.invoke('bonus-proxy')
      ])
      if (cancelled) return
      const count = (r) => {
        const o = (r.status === 'fulfilled' && !r.value.error) ? r.value.data?.opportunities : null
        return o ? o.length : null
      }
      setLiveStats({
        ev: count(ev),
        arb: count(arb),
        middle: count(middle),
        bonus: count(bonus)
      })
      setLiveStatsAt(Date.now())
    }
    loadLiveStats()
    return () => { cancelled = true }
  }, [isMobile, user?.id, isVisible])

  useEffect(() => {
    if (spotlightPaused) return
    if (spotlightIndex === SPOTLIGHT_TOOLS.length - 1) return
    const id = setInterval(() => {
      setSpotlightIndex(i => {
        if (i >= SPOTLIGHT_TOOLS.length - 1) return i
        return i + 1
      })
    }, 5000)
    return () => clearInterval(id)
  }, [spotlightPaused, spotlightIndex])

  const toggleOnboardingStep = async (stepId) => {
    if (!user?.id || !onboarding) return
    const willBeDone = !onboarding[stepId]
    const next = { ...onboarding, [stepId]: willBeDone }
    setOnboarding(next)
    if (willBeDone) {
      setJustTickedId(stepId)
      setTimeout(() => setJustTickedId(curr => (curr === stepId ? null : curr)), 400)
    }
    // Merge into the stored copy rather than this page's: tool pages write their
    // tour flags into the same column (under `tours`) after it was loaded here.
    const { data: latest } = await getUserProfile(user.id)
    const merged = { ...(latest?.onboarding_progress || {}), [stepId]: willBeDone }
    const { error } = await updateUserProfile(user.id, { onboarding_progress: merged })
    if (error) {
      setOnboarding(onboarding)
    }
  }

  const handleOnboardingNavigate = (target) => {
    if (target === '__discord__') {
      window.open('https://discord.gg/BV4dVEnx73', '_blank')
      return
    }
    if (onNavigate) onNavigate(target)
  }

  const showOnboarding = (() => {
    if (!user || onboarding === null) return false
    const allDone = ONBOARDING_STEPS.every(s => onboarding[s.id])
    if (allDone) return false
    return true
  })()
  const onboardingDoneCount = onboarding ? ONBOARDING_STEPS.filter(s => onboarding[s.id]).length : 0

  // Extract name from email or use user metadata
  const getUserName = () => {
    if (!user) return ''
    
    // First check if user has a name in metadata
    if (user.user_metadata?.full_name) {
      return user.user_metadata.full_name
    }
    if (user.user_metadata?.name) {
      return user.user_metadata.name
    }
    
    // Fallback to extracting name from email
    if (user.email) {
      const emailName = user.email.split('@')[0]
      // Capitalize first letter and replace dots/underscores with spaces
      return emailName
        .replace(/[._]/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    }
    
    return 'there'
  }

  const userName = getUserName()

  // Softer 400-weight accents, the same set the Racing Odds page uses.
  const UPDATE_TYPE_META = {
    feature:     { accent: '#34d399', label: 'New' },
    improvement: { accent: '#60a5fa', label: 'Improvement' },
    bugfix:      { accent: '#fb923c', label: 'Fix' },
    issue:       { accent: '#f87171', label: 'Issue' },
    maintenance: { accent: '#fbbf24', label: 'Maintenance' }
  }
  const getUpdateMeta = (type) => UPDATE_TYPE_META[type] || { accent: '#64748b', label: type }

  // One update as a hairline-separated row (Racing Odds style): the type in its
  // accent and the date on one quiet line, then the title and description.
  // Rows and dividers are .bc-update-row in the style block below.
  const renderUpdateItem = (update) => {
    const meta = getUpdateMeta(update.type)
    return (
      <div key={update.id} className="bc-update-row">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', fontSize: '11.5px', fontWeight: 550 }}>
          <span style={{ color: meta.accent }}>{meta.label}</span>
          <span style={{ color: '#4b5871' }}>{formatDate(update.created_at)}</span>
        </div>
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#e4e9f0', marginTop: '5px', letterSpacing: '-0.01em', lineHeight: 1.35 }}>
          {update.title}
        </div>
        {update.description ? (
          <div style={{ fontSize: '12.5px', lineHeight: 1.55, color: '#8391a7', marginTop: '3px' }}>
            {update.description}
          </div>
        ) : null}
      </div>
    )
  }

  // "27 Oct", with the year only when it isn't this year.
  const formatDate = (dateString) => {
    const d = new Date(dateString)
    return d.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      ...(d.getFullYear() !== new Date().getFullYear() && { year: 'numeric' })
    })
  }

  const features = [
    {
      id: 'ev',
      title: 'Expected Value Finder',
      description: 'Find positive expected value betting opportunities with real time odds',
      feature: 'ev',
      available: hasAccess('ev')
    },
    {
      id: 'arb',
      title: 'Arbitrage Finder',
      description: 'Discover risk free arbitrage opportunities across multiple bookmakers',
      feature: 'arb',
      available: hasAccess('arb')
    },
    {
      id: 'horse_bot',
      title: 'Horse Racing Bot',
      description: 'Real time +EV Horse Racing Selections delivered to Discord 2 minutes before each race',
      feature: 'horse_bot',
      available: true
    },
    {
      id: 'odds',
      title: 'Sports Odds',
      description: 'Compare live odds across all bookmakers in real time',
      feature: 'odds',
      available: hasAccess('odds')
    },
    {
      id: 'prop_ev',
      title: 'Prop Outliers',
      description: 'Player props priced against the market, with real time odds',
      feature: 'prop_ev',
      available: hasAccess('prop_ev'),
      releasedAt: '2026-04-30'
    },
    {
      id: 'tracker',
      title: 'Bet Tracker',
      description: 'Track and analyse your betting performance across EV, Arbitrage, Promo, and Middle bets',
      feature: 'tracker',
      available: hasAccess('tracker')
    }
  ]

  const isNewFeature = (releasedAt) => {
    if (!releasedAt) return false
    const released = new Date(releasedAt).getTime()
    if (Number.isNaN(released)) return false
    return Date.now() - released < 7 * 24 * 60 * 60 * 1000
  }

  const handleCardClick = (featureId) => {
    if (onNavigate) {
      onNavigate(featureId)
    }
  }

  const handleCardHover = (e) => {
    e.currentTarget.style.transform = 'translateY(-4px)'
    e.currentTarget.style.borderColor = '#2563eb'
    e.currentTarget.style.boxShadow = '0 8px 32px rgba(37, 99, 235, 0.2)'
  }

  const handleCardLeave = (e) => {
    e.currentTarget.style.transform = 'translateY(0)'
    e.currentTarget.style.borderColor = '#334155'
    e.currentTarget.style.boxShadow = 'none'
  }

  return (
    <div className="dashboard-container" style={{ ...dashboardStyles.container, backgroundColor: '#010714' }}>
      <style>{`
        .bc-mast { font-family: 'Geist', 'Inter', system-ui, sans-serif; font-variant-numeric: tabular-nums; }
        .bc-mast-date { font-size: 12.5px; font-weight: 500; color: #4b5871; margin-bottom: 6px; }
        .bc-mast-hello {
          margin: 0; font-weight: 650; letter-spacing: -0.03em; line-height: 1.15; color: #e4e9f0;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .bc-mast-plan {
          display: inline-flex; align-items: center; gap: 9px; height: 32px; padding: 0 12px; flex-shrink: 0; cursor: pointer;
          border-radius: 9px; background: #0d1424; border: 1px solid #1b2438; font: inherit; font-size: 12.5px; font-weight: 600;
          transition: border-color 0.15s;
        }
        .bc-mast-plan:hover { border-color: #35486b; }
        .bc-mast-plan-act { font-weight: 500; color: #4b5871; }
        .bc-mast-plan-act.is-upgrade { color: #60a5fa; }
        .bc-mast-icon {
          width: 32px; height: 32px; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
          border-radius: 9px; background: none; border: 1px solid #1b2438; color: #8391a7; transition: color 0.15s, border-color 0.15s;
        }
        .bc-mast-icon:hover { color: #e4e9f0; border-color: #35486b; }
        .bc-pulse {
          display: grid; margin-top: 16px; overflow: hidden;
          border-radius: 14px; background: rgba(13, 20, 36, 0.4); border: 1px solid #1b2438;
        }
        .bc-pulse-intro { display: flex; flex-direction: column; justify-content: center; gap: 4px; padding: 14px 18px; border-right: 1px solid #1b2438; }
        .bc-pulse-title { font-size: 13.5px; font-weight: 600; color: #e4e9f0; }
        .bc-pulse-sub { font-size: 12px; line-height: 1.4; color: #8391a7; }
        .bc-pulse-cell {
          position: relative; display: flex; flex-direction: column; align-items: flex-start; gap: 7px; padding: 14px 40px 15px 18px;
          border: 0; background: none; cursor: pointer; font: inherit; text-align: left; transition: background-color 0.15s;
        }
        .bc-pulse-cell + .bc-pulse-cell { border-left: 1px solid #1b2438; }
        .bc-pulse-cell:hover { background: #0d1424; }
        .bc-pulse-label { font-size: 12px; font-weight: 500; color: #8391a7; white-space: nowrap; }
        .bc-pulse-num { font-size: 26px; font-weight: 650; letter-spacing: -0.03em; line-height: 1; color: #e4e9f0; transition: color 0.15s; }
        .bc-pulse-cell:hover .bc-pulse-num { color: var(--accent); }
        .bc-pulse-hint { margin-top: -1px; font-size: 11.5px; font-weight: 500; color: var(--accent); opacity: 0.8; white-space: nowrap; }
        .bc-pulse-arrow { position: absolute; right: 16px; top: 50%; margin-top: -7px; color: #35486b; transition: color 0.15s, transform 0.15s; }
        .bc-pulse-cell:hover .bc-pulse-arrow { color: var(--accent); transform: translateX(3px); }
        .bc-pulse-skel {
          display: block; width: 46px; height: 26px; border-radius: 6px;
          background: linear-gradient(90deg, #0d1424 0%, #141e33 50%, #0d1424 100%); background-size: 200% 100%;
          animation: bcShimmer 1.4s linear infinite;
        }
        @keyframes bcShimmer { from { background-position: 100% 0; } to { background-position: -100% 0; } }
        @media (prefers-reduced-motion: reduce) { .bc-pulse-skel { animation: none; } }
        .bc-gs-head, .bc-gs-list { font-family: 'Geist', 'Inter', system-ui, sans-serif; font-variant-numeric: tabular-nums; }
        .bc-gs-head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 10px; }
        .bc-gs-title { margin: 0; font-size: 15px; font-weight: 650; color: #e4e9f0; line-height: 1.2; letter-spacing: -0.015em; }
        .bc-gs-count { font-size: 12px; color: #4b5871; }
        .bc-gs-progress { display: grid; grid-template-columns: repeat(5, 1fr); gap: 4px; margin-bottom: 8px; }
        .bc-gs-progress span { height: 3px; border-radius: 2px; background: #1b2438; transition: background-color 0.4s ease; }
        .bc-gs-progress span.is-done { background: #34d399; }
        .bc-gs-list { display: flex; flex-direction: column; }
        .bc-gs-step { display: flex; align-items: center; gap: 12px; padding: 11px 6px; border-radius: 10px; transition: background-color 0.2s; }
        .bc-gs-step + .bc-gs-step { border-top: 1px solid #1b2438; }
        .bc-gs-step.is-next { align-items: flex-start; padding: 13px 10px; margin: 2px 0; background: #0d1424; border-top-color: transparent; }
        .bc-gs-step.is-next + .bc-gs-step { border-top-color: transparent; }
        .bc-gs-check {
          width: 20px; height: 20px; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; padding: 0; cursor: pointer;
          border-radius: 50%; background: none; border: 1.5px solid #35486b; color: #05080f; transition: border-color 0.15s, background-color 0.15s;
        }
        .bc-gs-check:hover { border-color: #8391a7; }
        .bc-gs-step.is-next .bc-gs-check { margin-top: 1px; border-color: var(--accent); }
        .bc-gs-step.is-done .bc-gs-check { background: #34d399; border-color: #34d399; }
        .bc-gs-text { flex: 1; min-width: 0; }
        .bc-gs-name { display: flex; align-items: center; gap: 8px; font-size: 13.5px; font-weight: 600; color: #e4e9f0; line-height: 1.3; }
        .bc-gs-name svg { flex-shrink: 0; }
        .bc-gs-name span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .bc-gs-step.is-done .bc-gs-name { color: #4b5871; font-weight: 500; }
        .bc-gs-desc { margin-top: 4px; padding-left: 22px; font-size: 12.5px; line-height: 1.5; color: #8391a7; }
        .bc-gs-go {
          display: inline-flex; align-items: center; gap: 5px; flex-shrink: 0; height: 28px; padding: 0 10px; cursor: pointer;
          border-radius: 8px; background: none; border: 1px solid transparent; color: #8391a7;
          font: inherit; font-size: 12.5px; font-weight: 600; transition: color 0.15s, border-color 0.15s, background-color 0.15s;
        }
        .bc-gs-go:hover { color: #e4e9f0; border-color: #1b2438; }
        .bc-gs-step.is-next .bc-gs-go { height: 30px; padding: 0 12px; background: #080d18; border-color: color-mix(in srgb, var(--accent) 45%, transparent); color: var(--accent); }
        .bc-gs-step.is-next .bc-gs-go:hover { background: #05080f; }
        .bc-help { font-family: 'Geist', 'Inter', system-ui, sans-serif; box-sizing: border-box; }
        .bc-help-panel {
          display: flex; padding: 16px 18px; border-radius: 14px; background: rgba(13, 20, 36, 0.4); border: 1px solid #1b2438;
        }
        .bc-help-title { font-size: 14px; font-weight: 600; color: #e4e9f0; letter-spacing: -0.01em; }
        .bc-help-sub { margin-top: 3px; font-size: 12.5px; line-height: 1.45; color: #8391a7; }
        .bc-help-btn {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px; height: 36px; padding: 0 14px;
          border-radius: 9px; background: #0d1424; border: 1px solid #1b2438; color: #c9d2df;
          font-size: 13px; font-weight: 600; text-decoration: none; white-space: nowrap; transition: color 0.15s, border-color 0.15s;
        }
        .bc-help-btn:hover { color: #e4e9f0; border-color: #35486b; }
        .bc-spot { font-family: 'Geist', 'Inter', system-ui, sans-serif; font-variant-numeric: tabular-nums; }
        .bc-spot-step {
          width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
          border-radius: 8px; background: #0d1424; border: 1px solid #1b2438; color: #8391a7; transition: color 0.15s, border-color 0.15s;
        }
        .bc-spot-step:hover { color: #e4e9f0; border-color: #35486b; }
        .bc-spot-shot {
          display: block; width: 100%; height: 100%; object-fit: cover; object-position: top center;
          border-radius: 10px 10px 0 0; opacity: 0.92;
          -webkit-mask-image: linear-gradient(180deg, #000 0%, #000 45%, transparent 100%), linear-gradient(90deg, transparent 0%, #000 6%, #000 94%, transparent 100%);
          -webkit-mask-composite: source-in;
          mask-image: linear-gradient(180deg, #000 0%, #000 45%, transparent 100%), linear-gradient(90deg, transparent 0%, #000 6%, #000 94%, transparent 100%);
          mask-composite: intersect;
        }
        .bc-spot-cta {
          position: absolute; right: 14px; bottom: 14px; display: inline-flex; align-items: center; gap: 7px; cursor: pointer;
          padding: 8px 14px; border-radius: 9px; border: 1px solid; background: rgba(7, 12, 23, 0.92);
          font: inherit; font-size: 13px; font-weight: 600; transition: background-color 0.15s;
        }
        .bc-spot-cta:hover { background: rgba(13, 20, 36, 0.96); }
        .bc-update-row { padding: 12px 4px; font-family: 'Geist', 'Inter', system-ui, sans-serif; font-variant-numeric: tabular-nums; }
        .bc-update-row + .bc-update-row { border-top: 1px solid #1b2438; }
        @keyframes checkPop {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.25); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .check-pop { animation: checkPop 0.28s cubic-bezier(0.34, 1.56, 0.64, 1); }
        @keyframes spotlightFade {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes newBadgePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.55), 0 2px 10px rgba(16, 185, 129, 0.35); }
          50% { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0), 0 2px 14px rgba(16, 185, 129, 0.45); }
        }
        .new-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.1em;
          color: #ffffff;
          background: linear-gradient(135deg, #10b981 0%, #06b6d4 100%);
          padding: 4px 10px;
          border-radius: 999px;
          text-transform: uppercase;
          line-height: 1;
          border: 1px solid rgba(255, 255, 255, 0.18);
        }
        .new-badge-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: #ffffff;
          box-shadow: 0 0 6px rgba(255, 255, 255, 0.9);
        }
        .new-badge-desktop {
          position: absolute;
          top: 12px;
          right: 12px;
          animation: newBadgePulse 2.2s ease-in-out infinite;
        }
        .new-badge-mobile {
          font-size: 9px;
          padding: 3px 8px;
          gap: 4px;
        }
        .new-badge-mobile .new-badge-dot {
          width: 4px;
          height: 4px;
        }
        @media (prefers-reduced-motion: reduce) {
          .new-badge-desktop { animation: none; }
        }

        /* Global body scrollbar styling */
        body::-webkit-scrollbar,
        html::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        body::-webkit-scrollbar-track,
        html::-webkit-scrollbar-track {
          background: #010714;
        }
        body::-webkit-scrollbar-thumb,
        html::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 3px;
          transition: background 0.2s;
        }
        body:hover::-webkit-scrollbar-thumb,
        html:hover::-webkit-scrollbar-thumb {
          background: #475569;
        }
        body::-webkit-scrollbar-thumb:hover,
        html::-webkit-scrollbar-thumb:hover {
          background: #64748b !important;
        }

        /* Custom scrollbar for hover-only visibility */
        .dashboard-scrollable::-webkit-scrollbar,
        .dashboard-container::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .dashboard-scrollable::-webkit-scrollbar-track,
        .dashboard-container::-webkit-scrollbar-track {
          background: #010714;
        }
        .dashboard-scrollable::-webkit-scrollbar-thumb,
        .dashboard-container::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 3px;
          transition: background 0.2s;
        }
        .dashboard-scrollable:hover::-webkit-scrollbar-thumb,
        .dashboard-container:hover::-webkit-scrollbar-thumb {
          background: #475569;
        }
        .dashboard-scrollable::-webkit-scrollbar-thumb:hover,
        .dashboard-container::-webkit-scrollbar-thumb:hover {
          background: #64748b !important;
        }

        /* Firefox */
        body,
        html {
          scrollbar-width: thin;
          scrollbar-color: transparent #010714;
        }
        body:hover,
        html:hover {
          scrollbar-color: #475569 #010714;
        }
        .dashboard-scrollable,
        .dashboard-container {
          scrollbar-width: thin;
          scrollbar-color: transparent #010714;
        }
        .dashboard-scrollable:hover,
        .dashboard-container:hover {
          scrollbar-color: #475569 #010714;
        }
      `}</style>
      {(() => {
        const tierStyles = {
          free:         { color: '#94a3b8', label: 'Free Plan' },
          sports:       { color: '#60a5fa', label: 'Sports Premium' },
          horse_racing: { color: '#a78bfa', label: 'Promo Premium' },
          full_access:  { color: '#34d399', label: 'Full Access' }
        }
        const tier = tierStyles[subscriptionTier] || tierStyles.free
        const hour = new Date().getHours()
        const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'
        const dateStr = new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })
        const isFree = subscriptionTier === 'free'
        const handlePlanClick = () => {
          if (isFree) onNavigate('pricing')
          else goToCustomerPortal()
        }
        const iconBtn = (props) => (
          <button className="bc-mast-icon" onClick={props.onClick} title={props.title} aria-label={props.title}>
            {props.children}
          </button>
        )

        return (
          <>
            {/* Masthead, Racing Odds look: Geist, one hairline under it, quiet controls,
                colour only where it means something (the plan, a hovered count). Styles
                are the .bc-mast* / .bc-pulse* rules in the style block. */}
            <div className="bc-mast" style={{
              maxWidth: '1280px',
              margin: isMobile ? '0' : '32px auto 24px',
              padding: isMobile ? '0' : '0 12px',
              boxSizing: 'border-box'
            }}>
            <div style={{
              padding: isMobile ? '20px 20px 12px' : '0 0 18px',
              borderBottom: '1px solid #1b2438',
              display: 'flex',
              alignItems: isMobile ? 'flex-start' : 'flex-end',
              gap: isMobile ? '12px' : '14px'
            }}>
              {/* Greeting: stacked on mobile (matches app), date over greeting on desktop */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="bc-mast-date">{isMobile ? greeting : dateStr}</div>
                <h1 className="bc-mast-hello" style={{ fontSize: isMobile ? '24px' : '28px' }}>
                  {isMobile ? (userName || 'Welcome back') : `${greeting}${userName ? `, ${userName}` : ''}`}
                </h1>
              </div>

              {/* Plan: the plan's own colour, then what clicking it does */}
              {user && (
                <button
                  className="bc-mast-plan"
                  onClick={handlePlanClick}
                  title={isFree ? 'Upgrade your plan' : 'Manage subscription'}
                  style={{ color: tier.color }}
                >
                  {isMobile ? tier.label.replace(' Premium', '').replace(' Plan', '') : tier.label}
                  {!isMobile && <span className={`bc-mast-plan-act${isFree ? ' is-upgrade' : ''}`}>{isFree ? 'Upgrade' : 'Manage'}</span>}
                </button>
              )}

              {/* Action icons (desktop only — mobile keeps it tight) */}
              {!isMobile && user && (
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  {iconBtn({
                    title: 'Join our Discord',
                    onClick: () => window.open('https://discord.gg/BV4dVEnx73', '_blank'),
                    children: (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
                      </svg>
                    )
                  })}
                  {iconBtn({
                    title: 'Profile & settings',
                    onClick: () => onNavigate('profile'),
                    children: (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3"/>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                      </svg>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Market pulse: live counts from the same proxies the tools use, as one
                strip of figures, each with what it's worth to the user. Each opens its
                tool; free users land on the preview wall (the real upsell). Skeletons
                until the first check lands, then the numbers count up once. */}
            {!isMobile && user && (() => {
              const loaded = liveStatsAt != null
              const pulse = [
                { key: 'ev', label: '+EV bets', hint: 'Beat the market', accent: '#34d399', target: 'ev' },
                { key: 'arb', label: 'Arbs', hint: 'Guaranteed profit', accent: '#a78bfa', target: 'arb' },
                { key: 'middle', label: 'Middles', hint: 'Win both sides', accent: '#38bdf8', target: 'middles' },
                { key: 'bonus', label: 'Bonus turnover', hint: 'Convert bonus bets', accent: '#fbbf24', target: 'bonus' }
              ].filter(p => !loaded || liveStats[p.key] !== null)
              if (loaded && pulse.length === 0) return null
              return (
                <div className="bc-pulse" style={{ gridTemplateColumns: `200px repeat(${pulse.length}, minmax(0, 1fr))` }}>
                  <div className="bc-pulse-intro">
                    <span className="bc-pulse-title">Right now</span>
                    <span className="bc-pulse-sub">Bets our tools have found</span>
                  </div>
                  {pulse.map(p => (
                    <button key={p.key} className="bc-pulse-cell" style={{ '--accent': p.accent }} onClick={() => onNavigate(p.target)}>
                      <span className="bc-pulse-label">{p.label}</span>
                      {loaded ? <span className="bc-pulse-num"><CountUp value={liveStats[p.key]} /></span> : <span className="bc-pulse-skel" />}
                      <span className="bc-pulse-hint">{p.hint}</span>
                      <svg className="bc-pulse-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </button>
                  ))}
                </div>
              )
            })()}
            </div>

            {/* Trial / upgrade banner — free users only */}
            {isFree && user && !authLoading && (() => {
              const trial = !hadTrial
              const bannerTitle = trial ? 'Start your free trial' : 'Upgrade to unlock everything'
              const bannerSub = trial
                ? 'Free for 7 days · cancel anytime · full access to every tool'
                : 'Unlock every tool, no limits — pick a plan that fits.'
              const bannerCta = trial ? 'Start free trial' : 'See plans'

              // ─── Mobile: app-style gradient-bordered card with CTA strip ───
              if (isMobile) {
                return (
                  <div style={{ margin: '20px 16px 20px' }}>
                    {/* Gradient border via a 1.5px gradient pad */}
                    <div style={{
                      borderRadius: '20px',
                      padding: '1.5px',
                      background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #8b5cf6 100%)',
                      boxShadow: '0 6px 18px rgba(59, 130, 246, 0.35)'
                    }}>
                      <button
                        onClick={() => onNavigate('pricing')}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: 0,
                          border: 'none',
                          borderRadius: '18px',
                          background: '#0e1830',
                          color: 'inherit',
                          cursor: 'pointer',
                          overflow: 'hidden',
                          position: 'relative',
                          fontFamily: 'inherit'
                        }}
                      >
                        {/* Glow blob, top-right */}
                        <div style={{
                          position: 'absolute', top: '-50px', right: '-30px',
                          width: '200px', height: '200px', borderRadius: '100px',
                          background: 'radial-gradient(circle, rgba(96,165,250,0.35) 0%, transparent 70%)',
                          pointerEvents: 'none'
                        }} />

                        {/* Top row: icon + text + chevron */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '16px' }}>
                          <div style={{
                            width: '48px', height: '48px', minWidth: '48px',
                            borderRadius: '16px',
                            border: '1px solid rgba(96,165,250,0.5)',
                            background: 'rgba(59,130,246,0.18)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              {trial ? (
                                <>
                                  <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
                                  <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
                                  <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
                                  <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
                                </>
                              ) : (
                                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                              )}
                            </svg>
                          </div>

                          <div style={{ flex: 1, minWidth: 0 }}>
                            {trial && (
                              <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                marginBottom: '6px',
                                borderRadius: '999px',
                                background: 'linear-gradient(135deg, rgba(52,211,153,0.22) 0%, rgba(16,185,129,0.12) 100%)',
                                border: '1px solid rgba(52,211,153,0.35)',
                                boxShadow: '0 0 10px rgba(52,211,153,0.18)',
                                padding: '3px 9px'
                              }}>
                                <span style={{ fontSize: '9px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#6ee7b7' }}>
                                  Limited offer
                                </span>
                              </div>
                            )}
                            <div style={{ fontSize: '16px', fontWeight: '800', color: '#ffffff', lineHeight: 1.2 }}>
                              {bannerTitle}
                            </div>
                            <div style={{ fontSize: '11.5px', lineHeight: '16px', color: '#94a3b8', marginTop: '2px' }}>
                              {bannerSub}
                            </div>
                          </div>

                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                            <polyline points="9 18 15 12 9 6"/>
                          </svg>
                        </div>

                        {/* CTA strip */}
                        <div style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                          borderTop: '1px solid #1e2a44',
                          background: '#10203f',
                          padding: '10px 0'
                        }}>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: '#93c5fd' }}>{bannerCta}</span>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12"/>
                            <polyline points="12 5 19 12 12 19"/>
                          </svg>
                        </div>
                      </button>
                    </div>
                  </div>
                )
              }

              // ─── Desktop: original slim banner ───
              return (
              <div style={{
                maxWidth: '1280px',
                margin: '0 auto 28px',
                padding: '0 12px',
                boxSizing: 'border-box'
              }}>
              <div style={{
                padding: '14px 22px',
                background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.15) 0%, rgba(124, 58, 237, 0.1) 100%)',
                border: '1px solid rgba(96, 165, 250, 0.35)',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap',
              }}>
                <div style={{
                  width: '32px', height: '32px',
                  minWidth: '32px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#f1f5f9', lineHeight: 1.2 }}>
                    {trial ? 'Start your 7-day free trial' : 'Upgrade to unlock everything'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#8b97ab', marginTop: '2px' }}>
                    {trial ? 'Unlock every tool. Cancel anytime.' : 'Unlock every tool, no limits — pick a plan that fits.'}
                  </div>
                </div>
                <button
                  onClick={() => onNavigate('pricing')}
                  style={{
                    padding: '9px 18px',
                    background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    letterSpacing: '0.02em',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.4)',
                    transition: 'all 0.15s ease',
                    flexShrink: 0,
                    fontFamily: 'inherit'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.5)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.4)' }}
                >
                  {trial ? 'Start trial →' : 'See plans →'}
                </button>
              </div>
              </div>
              )
            })()}
          </>
        )
      })()}

      {(() => {
        // The trial/upgrade banner sits between the greeting and this block for
        // free users. When it's absent (paid users), add top spacing so this
        // section doesn't butt against the greeting.
        const hasBanner = subscriptionTier === 'free' && user && !authLoading
        const mobileTop = hasBanner ? '0' : '20px'
        return (
        <div style={{
          maxWidth: '1280px',
          margin: isMobile ? `${mobileTop} 12px 24px` : '0 auto 32px',
          padding: isMobile ? '0' : '0 12px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '12px' : '20px',
          alignItems: 'stretch'
        }}>
        <div style={{
          flex: isMobile ? 'none' : '1 1 50%',
          order: isMobile && !showOnboarding ? 2 : 0,
          padding: '14px',
          background: 'rgba(13, 20, 36, 0.4)',
          border: '1px solid #1b2438',
          borderRadius: '14px',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: isMobile ? 'none' : '470px'
        }}>
          {showOnboarding ? (
          <>
          {/* Getting started, Racing Odds style: one segment per step, hairline rows.
              The next step opens up with its description and a Start button; done
              steps go quiet. Styles are .bc-gs* in the style block. */}
          <div className="bc-gs-head">
            <h2 className="bc-gs-title">Getting started</h2>
            <span className="bc-gs-count">{onboardingDoneCount} of {ONBOARDING_STEPS.length} done</span>
          </div>
          <div className="bc-gs-progress" aria-hidden="true">
            {ONBOARDING_STEPS.map(step => (
              <span key={step.id} className={onboarding?.[step.id] ? 'is-done' : undefined} />
            ))}
          </div>
          <div className="bc-gs-list">
            {(() => {
              const nextStepId = ONBOARDING_STEPS.find(s => !onboarding?.[s.id])?.id
              return ONBOARDING_STEPS.map(step => {
                const done = !!onboarding?.[step.id]
                const isNext = step.id === nextStepId
                return (
                  <div key={step.id} className={`bc-gs-step${done ? ' is-done' : ''}${isNext ? ' is-next' : ''}`} style={{ '--accent': step.accent }}>
                    <button
                      className="bc-gs-check"
                      onClick={() => toggleOnboardingStep(step.id)}
                      aria-pressed={done}
                      aria-label={done ? `Mark "${step.shortTitle}" as not done` : `Mark "${step.shortTitle}" as done`}
                    >
                      {done && (
                        <svg className={justTickedId === step.id ? 'check-pop' : ''} width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </button>
                    <div className="bc-gs-text">
                      <div className="bc-gs-name">
                        <DashboardIcon name={step.icon} size={14} color={isNext ? step.accent : done ? '#35486b' : '#8391a7'} />
                        <span>{step.shortTitle}</span>
                      </div>
                      {isNext && <div className="bc-gs-desc">{step.desc}</div>}
                    </div>
                    {!done && (
                      <button className="bc-gs-go" onClick={() => handleOnboardingNavigate(step.target)}>
                        {isNext ? 'Start' : 'Go'}
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                      </button>
                    )}
                  </div>
                )
              })
            })()}
          </div>
          </>
          ) : (
          <>
            <div style={{ marginBottom: '12px' }}>
              <h2 style={{ fontSize: '15px', fontWeight: 650, color: '#e4e9f0', margin: 0, lineHeight: 1.2, letterSpacing: '-0.015em' }}>
                Latest updates
              </h2>
            </div>
            <div className="dashboard-scrollable" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', minHeight: 0, paddingRight: '6px' }}>
              {updatesLoading ? (
                <div style={{ textAlign: 'center', color: '#8391a7', fontSize: '13px', padding: '20px' }}>Loading updates…</div>
              ) : updates.length > 0 ? (
                updates.map(update => renderUpdateItem(update))
              ) : (
                <div style={{ textAlign: 'center', color: '#8391a7', fontSize: '13px', padding: '20px' }}>No updates yet</div>
              )}
            </div>
          </>
          )}
        </div>
        {(() => {
          // Featured tools, Racing Odds style: neutral panel, sentence-case heading
          // with a counter, small step buttons, the tool's accent kept to its icon
          // and the button. No dot pager or glow.
          const spotlight = SPOTLIGHT_TOOLS[spotlightIndex]
          const goPrev = () => setSpotlightIndex(i => (i - 1 + SPOTLIGHT_TOOLS.length) % SPOTLIGHT_TOOLS.length)
          const goNext = () => setSpotlightIndex(i => (i + 1) % SPOTLIGHT_TOOLS.length)
          return (
            <div
              className="bc-spot"
              onMouseEnter={() => setSpotlightPaused(true)}
              onMouseLeave={() => setSpotlightPaused(false)}
              style={{
                flex: '1 1 50%',
                padding: '14px',
                background: 'rgba(13, 20, 36, 0.4)',
                border: '1px solid #1b2438',
                borderRadius: '14px',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Header: heading + counter, step buttons */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                  <h2 style={{ fontSize: '15px', fontWeight: 650, color: '#e4e9f0', margin: 0, lineHeight: 1.2, letterSpacing: '-0.015em' }}>
                    Featured tools
                  </h2>
                  <span style={{ fontSize: '12px', color: '#4b5871' }}>{spotlightIndex + 1} / {SPOTLIGHT_TOOLS.length}</span>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button className="bc-spot-step" onClick={goPrev} aria-label="Previous tool">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <button className="bc-spot-step" onClick={goNext} aria-label="Next tool">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                </div>
              </div>

              {/* Body: icon + title, tagline */}
              <div key={spotlight.id} style={{ animation: 'spotlightFade 0.4s ease', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <DashboardIcon name={spotlight.id} size={22} color={spotlight.accent} />
                  <h3 style={{ fontSize: isMobile ? '18px' : '21px', fontWeight: 650, color: '#e4e9f0', margin: 0, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                    {spotlight.title}
                  </h3>
                </div>
                <p style={{ fontSize: '13px', color: '#8391a7', lineHeight: 1.55, margin: '6px 0 0' }}>
                  {isMobile ? (spotlight.tagline.match(/^[^.!?]+[.!?]/)?.[0] || spotlight.tagline) : spotlight.tagline}
                </p>
              </div>

              {/* Tool preview: no frame, the screenshot fades out into the panel (.bc-spot-shot).
                  CTA on a dark backing so it reads over any screenshot. */}
              {spotlight.image && (
                <div style={{
                  flex: 1,
                  minHeight: isMobile ? '280px' : '300px',
                  maxHeight: isMobile ? '280px' : '300px',
                  position: 'relative'
                }}>
                  <img
                    key={spotlight.id}
                    src={isMobile && spotlight.imageMobile ? spotlight.imageMobile : spotlight.image}
                    alt={`${spotlight.title} preview`}
                    onError={(e) => { e.currentTarget.style.visibility = 'hidden' }}
                    loading="lazy"
                    className="bc-spot-shot"
                  />
                  <button
                    className="bc-spot-cta"
                    onClick={() => onNavigate && onNavigate(spotlight.id)}
                    style={{ color: spotlight.accent, borderColor: `${spotlight.accent}66` }}
                  >
                    {spotlight.cta}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"/>
                      <polyline points="12 5 19 12 12 19"/>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )
        })()}
      </div>
      ) })()}

      {false && isMobile && (
      <div style={{ ...dashboardStyles.grid, padding: isMobile ? '0 12px' : '0 40px', marginTop: '24px', gap: isMobile ? '10px' : '24px', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)' }}>
        {features.map(feature => (
          isMobile ? (
            /* Mobile card — clean pill row, no button, no description */
            <div
              key={feature.id}
              onClick={() => handleCardClick(feature.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '14px 14px',
                background: 'linear-gradient(135deg, #1e293b 0%, #1a2332 100%)',
                border: '1px solid #2a3a52',
                borderRadius: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.background = 'linear-gradient(135deg, #1e3a5f 0%, #1e293b 100%)' }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a3a52'; e.currentTarget.style.background = 'linear-gradient(135deg, #1e293b 0%, #1a2332 100%)' }}
            >
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', backgroundColor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <DashboardIcon name={feature.id} size={20} color='#94a3b8' />
              </div>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#e2e8f0', flex: 1, lineHeight: '1.3' }}>{feature.title}</span>
              {isNewFeature(feature.releasedAt) && (
                <span className="new-badge new-badge-mobile">
                  <span className="new-badge-dot" />
                  NEW
                </span>
              )}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </div>
          ) : (
            /* Desktop card — unchanged */
            <div
              key={feature.id}
              style={{ ...dashboardStyles.card, position: 'relative' }}
              onClick={() => handleCardClick(feature.id)}
              onMouseEnter={handleCardHover}
              onMouseLeave={handleCardLeave}
            >
              {isNewFeature(feature.releasedAt) && (
                <span className="new-badge new-badge-desktop">
                  <span className="new-badge-dot" />
                  NEW
                </span>
              )}
              <div style={dashboardStyles.cardIcon}>
                <DashboardIcon name={feature.id} size={48} color='#ffffff' />
              </div>
              <h3 style={dashboardStyles.cardTitle}>{feature.title}</h3>
              <p style={dashboardStyles.cardDescription}>{feature.description}</p>
              <button
                style={dashboardStyles.cardButton}
                onMouseEnter={(e) => { e.target.style.backgroundColor = '#1d4ed8' }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = '#2563eb' }}
              >
                Open {feature.title}
              </button>
            </div>
          )
        ))}
      </div>
      )}

      {/* Discord Integration Section — checklist now covers this */}
      {false && isMobile && (
      <div id="discord-section" style={{
        ...dashboardStyles.upgradeSection,
        margin: isMobile ? '24px 12px 0' : '48px auto',
        padding: isMobile ? '20px 16px' : '32px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          marginBottom: '16px'
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="#5865F2">
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
          </svg>
          <h2 style={dashboardStyles.upgradeTitle}>Join Our Discord Community</h2>
        </div>
        <p style={dashboardStyles.upgradeDescription}>
          Connect with other winning bettors in our exclusive Discord community. Get instant access to community chats, live support, custom betting bots and more based on your subscription tier.
        </p>
        <div style={{
          backgroundColor: '#2f3349',
          border: '1px solid #5865F2',
          borderRadius: '8px',
          padding: '20px',
          margin: '20px 0',
          textAlign: 'left'
        }}>
          <h4 style={{
            color: '#f1f5f9',
            fontSize: '16px',
            fontWeight: '600',
            margin: '0 0 12px 0'
          }}>
            How to Link Your Discord:
          </h4>
          <ol style={{
            color: '#cbd5e1',
            fontSize: '14px',
            lineHeight: '1.6',
            paddingLeft: '20px'
          }}>
            <li>Join our Discord server</li>
            <li>Use the <code style={{
              backgroundColor: '#1e293b',
              padding: '2px 6px',
              borderRadius: '4px',
              color: '#60a5fa'
            }}>/verify</code> command in any channel</li>
            <li>Enter your Bookie Charity account email when prompted</li>
            <li>Get your subscription based role automatically!</li>
          </ol>
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#1e293b',
            borderRadius: '6px',
            border: '1px solid #334155'
          }}>
            <p style={{
              color: '#94a3b8',
              fontSize: '13px',
              margin: '0 0 8px 0'
            }}>
              <strong>Your Discord Role:</strong>
            </p>
{subscriptionTier === 'full_access' ? (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  backgroundColor: 'rgba(107, 250, 5, 0.2)',
                  color: '#6bfa05',
                  border: '1px solid #6bfa05'
                }}>
                  Sports
                </span>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  backgroundColor: 'rgba(255, 0, 4, 0.2)',
                  color: '#ff0004',
                  border: '1px solid #ff0004'
                }}>
                  Promo
                </span>
              </div>
            ) : (
              <span style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600',
                backgroundColor: subscriptionTier === 'sports' ? 'rgba(107, 250, 5, 0.2)' :
                                subscriptionTier === 'horse_racing' ? 'rgba(255, 0, 4, 0.2)' : '#374151',
                color: subscriptionTier === 'sports' ? '#6bfa05' :
                       subscriptionTier === 'horse_racing' ? '#ff0004' : '#9ca3af',
                border: subscriptionTier === 'sports' ? '1px solid #6bfa05' :
                        subscriptionTier === 'horse_racing' ? '1px solid #ff0004' : '1px solid #374151'
              }}>
                {subscriptionTier === 'sports' ? 'Sports' :
                 subscriptionTier === 'horse_racing' ? 'Promo' :
                 'None (Free Plan)'}
              </span>
            )}
          </div>
        </div>
        <button
          style={{
            ...dashboardStyles.upgradeButton,
            backgroundColor: '#5865F2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            margin: '0 auto'
          }}
          onClick={() => window.open('https://discord.gg/BV4dVEnx73', '_blank')}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#4752C4'
            e.target.style.transform = 'translateY(-2px)'
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#5865F2'
            e.target.style.transform = 'translateY(0)'
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
          </svg>
          Join Discord Server
        </button>
      </div>
      )}

      {/* Latest Updates Section — hidden once onboarding is done because the swap slot above now shows updates */}
      {showOnboarding && (
      <div style={{
        maxWidth: '1280px',
        margin: isMobile ? '28px 16px 0' : '32px auto 0',
        padding: isMobile ? '0' : '0 12px'
      }}>
        <h2 style={{ fontSize: '15px', fontWeight: 650, color: '#e4e9f0', margin: '0 0 12px 0', lineHeight: 1.2, letterSpacing: '-0.015em' }}>
          Latest updates
        </h2>
        {updatesLoading ? (
          <div style={{ textAlign: 'center', color: '#8391a7', fontSize: '13px', padding: '24px' }}>
            Loading updates…
          </div>
        ) : updates.length > 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            background: 'rgba(13, 20, 36, 0.4)',
            border: '1px solid #1b2438',
            borderRadius: '14px',
            padding: '2px 14px'
          }}>
            {updates.map(update => renderUpdateItem(update))}
          </div>
        ) : (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            background: '#0f172a',
            border: '1px solid #1b2438',
            borderRadius: '14px',
            padding: '32px 0'
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              <path d="M18.63 13A17.89 17.89 0 0 1 18 8"/>
              <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14"/>
              <path d="M18 8a6 6 0 0 0-9.33-5"/>
              <line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
            <span style={{ marginTop: '8px', fontSize: '13px', color: '#8391a7' }}>No updates yet</span>
          </div>
        )}
      </div>
      )}

      {/* Help footer: the same quiet panel as the sections above (.bc-help* in the style block) */}
      <div className="bc-help" style={{
        maxWidth: '1280px',
        margin: isMobile ? '24px 12px 24px' : '40px auto 28px',
        padding: isMobile ? '0' : '0 12px'
      }}>
        <div className="bc-help-panel" style={{
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? '14px' : '20px'
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="bc-help-title">Need a hand?</div>
            <div className="bc-help-sub">Message the team on Discord or email us anytime.</div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <a className="bc-help-btn" href="https://discord.gg/BV4dVEnx73" target="_blank" rel="noopener noreferrer" style={{ flex: isMobile ? 1 : 'none' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
          </svg>
              Discord
            </a>
            <a className="bc-help-btn" href="mailto:support@bookiecharity.com" title="support@bookiecharity.com" style={{ flex: isMobile ? 1 : 'none' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              Email us
            </a>
          </div>
        </div>
      </div>

    </div>
  )
}